import { useEffect, useState } from "react";
import { History } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtRp, fmtNum } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MoneyInput } from "@/components/ui/money-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const fmtTime = (s) => new Date(s).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" });

function PriceHistory({ items }) {
  if (!items?.length) return <p className="text-xs text-slate-400" data-testid="price-history-empty">Belum pernah diubah.</p>;
  return (
    <ul className="space-y-2 max-h-48 overflow-y-auto" data-testid="price-history-list">
      {[...items].reverse().map((h, i) => (
        <li key={i} className="text-xs border border-slate-100 rounded-lg px-3 py-2 bg-slate-50" data-testid={`price-history-${i}`}>
          <div className="font-mono"><span className="text-slate-500 line-through">{h.old_price ? fmtRp(h.old_price) : "—"}</span> → <b className="text-amber-700">{fmtRp(h.new_price)}</b></div>
          <div className="text-slate-600 mt-0.5">"{h.reason}"</div>
          <div className="text-slate-400 mt-0.5">{h.by} ({h.role === "owner" ? "Pemilik" : "Admin"}) • {fmtTime(h.at)}</div>
        </li>
      ))}
    </ul>
  );
}

export function EditPriceDialog({ tx, onClose, onSaved }) {
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (tx) { setPrice(tx.unit_price ? String(Math.round(tx.unit_price)) : ""); setReason(""); } }, [tx]);
  const unit = tx?.unit || "MC";
  const valid = Number(price) > 0 && reason.trim().length >= 5 && Math.round(Number(price)) !== Math.round(tx?.unit_price || 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/transactions/${tx.id}/price`, { unit_price: Number(price), reason: reason.trim() });
      toast.success("Harga jual diperbarui — jurnal disesuaikan");
      onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="edit-price-dialog">
        <DialogHeader>
          <DialogTitle>Ubah Harga Jual</DialogTitle>
          <DialogDescription className="font-mono">{tx?.receipt_no} • {tx?.batch_code} <span className="font-sans">{tx?.species_name} {tx?.size} • {fmtNum(tx?.quantity_mc)} {unit}</span></DialogDescription>
        </DialogHeader>
        {tx && (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Harga saat ini/{unit}</Label><div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 font-mono text-sm" data-testid="edit-price-current">{tx.unit_price ? fmtRp(tx.unit_price) : "—"}</div></div>
              <div className="space-y-1.5"><Label>Harga baru/{unit} <span className="text-rose-500">*</span></Label><MoneyInput value={price} onChange={setPrice} data-testid="input-edit-price" /></div>
            </div>
            {Number(price) > 0 && <p className="text-xs text-sky-700 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2" data-testid="edit-price-total">Total baru <b>{fmtRp(Number(price) * tx.quantity_mc)}</b>{tx.total_value ? <> (sebelumnya {fmtRp(tx.total_value)})</> : null}</p>}
            <div className="space-y-1.5"><Label>Alasan perubahan <span className="text-rose-500">*</span></Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="cth. Negosiasi ulang dengan pembeli, salah input" rows={2} data-testid="input-edit-price-reason" />{reason && reason.trim().length < 5 && <p className="text-[11px] text-rose-600">Minimal 5 karakter</p>}</div>
            <div className="space-y-1.5"><Label className="inline-flex items-center gap-1"><History className="h-3.5 w-3.5" /> Riwayat perubahan</Label><PriceHistory items={tx.price_history} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} data-testid="btn-edit-price-cancel">Batal</Button>
              <Button type="submit" disabled={saving || !valid} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-edit-price-submit">{saving ? "Menyimpan..." : "Simpan Perubahan"}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
